import {createAction, createAsyncThunk} from '@reduxjs/toolkit'
import {go, RouterState} from 'redux-first-history'

import {isApiError} from '@shared/helpers/api'
import {qs} from '@shared/helpers/queryString'

import {changeLocation, systemAlert} from '@web/actions/application'
import {
    ChangeDeliveryRequestFailMessage,
    DefaultAddressBookRequestFailMessage,
    DELIVERY_MANAGER_COMPLETED,
    unsafeUrlMessage,
} from '@web/core/constants'
import {getPathNameInfo, isSafeUrl} from '@web/helpers/path'
import {isMobileDisplay, isNaverShopping} from '@web/helpers/userAgent'
import {loggingAction} from '@web/middlewares/logger'
import {commonSelector, CommonState, orderPhaseInfoSelector, pageSetInfoSelector} from '@web/slices/common/slice'
import {
    AddressBy,
    baseAddressInfoSelector,
    BaseDeliveryAddress,
    checkReturnRecords,
    clearStorage,
    closeDeliveryManagerWithFormConfirmTypes,
    DELIVERY_MANAGER_TYPE,
    deliveryInfoPayloadSelector,
    deliveryManagerSelector,
    DeliveryManagerState,
    getInitialAddressSearchQuery,
    initWithAddressSearchCase,
    initWithDeliveryAddressListCase,
    PersonalCustomsErrorCode,
    saveDeliveryManagerSessionStorage,
    saveStoragePayloadSelector,
} from '@web/slices/deliveryManager/slice'

import orderManagerApis from './apis/orderManager'
import {sendIframePostmessage} from './utils'

type AddressApplyPayload = {
    type: 'address'
    content: {
        baseAddress: string
        detailAddress: string
        isRoadNameAddress: boolean
        numberOfLotAddress: string
        roadNameAddress: string
        zipcode: string
        legalRegionCode: string
        roadGroupId: string
        buildingNum: string
    }
}

type DeliveryAddressApplyPayload = {
    type: 'deliveryAddress'
    content: BaseDeliveryAddress
}

type DeliveryInfoApplyPayload = {
    type: 'deliveryInfo'
    content: {
        deliveryAddress: BaseDeliveryAddress
        isParticularInput: boolean
        memos: {
            productOrderNo: string
            memo: string
        }[]
    }
}

type ApplyPayload = AddressApplyPayload | DeliveryAddressApplyPayload | DeliveryInfoApplyPayload

export const applyAddress = createAsyncThunk<
    Maybe<ApplyPayload>,
    {addressBy?: AddressBy},
    {
        state: {
            common: CommonState
            deliveryManager: DeliveryManagerState
        }
        rejectValue: {code?: string; message?: string}
    }
>('deliveryManager/applyAddress', async ({addressBy}, {getState, dispatch, rejectWithValue}) => {
    const {
        address,
        options: {type, returnUrl, postMessageKey},
    } = deliveryManagerSelector(getState())
    const {seqID} = commonSelector(getState())

    const {addressReturnable, deliveryAddressReturnable, deliveryInfoReturnable} = checkReturnRecords(type || '')

    // 주소 적용 (우편번호검색)
    if (addressReturnable) {
        const content = {
            baseAddress: address?.roadAddress || address?.jibunAddress || '',
            detailAddress: address?.detailAddress || '',
            isRoadNameAddress: !!address?.roadAddress,
            numberOfLotAddress: address?.jibunAddress ?? '',
            roadNameAddress: address?.roadAddress ?? '',
            zipcode: address?.roadZipcode || address?.zipcode || '',
            legalRegionCode: address?.legalRegionCode ?? '',
            roadGroupId: address?.roadGroupId ?? '',
            buildingNum: address?.buildingNum ?? '',
            buildingManagementNo: address?.buildingManagementNo ?? '',
            locationX: address?.locationX ?? '',
            locationY: address?.locationY ?? '',
        }

        if (returnUrl) {
            if (!isSafeUrl(returnUrl)) {
                dispatch(
                    loggingAction({
                        type: 'info',
                        info: {
                            message: unsafeUrlMessage,
                        },
                    }),
                )
                return rejectWithValue({message: unsafeUrlMessage})
            }

            const {url, query: rurlQuery} = qs.parseUrl(returnUrl)
            // rurl queryString으로 데이터를 보내야 하므로, 닫기도 여기서 처리.
            dispatch(changeLocation({type: 'PUSH', url: qs.stringifyUrl({url, query: {...rurlQuery, ...content}})}))
            return {type: 'address', content}
        }

        if (postMessageKey) {
            const isIframEnv = window.location !== window.parent.location
            if (isIframEnv) {
                sendIframePostmessage({key: postMessageKey, content})
                return {type: 'address', content}
            }

            window.opener?.postMessage(
                {
                    type: postMessageKey,
                    content,
                },
                '*',
            )
            return {type: 'address', content}
        }
    }

    // 배송지 적용 (주문서)
    if (deliveryAddressReturnable) {
        const deliveryAddress = baseAddressInfoSelector(getState())

        if (!isMobileDisplay) {
            window.opener?.postMessage({
                key: DELIVERY_MANAGER_COMPLETED,
                content: deliveryAddress,
            })
        }
        return {type: 'deliveryAddress', content: deliveryAddress}
    }

    // 배송정보 적용 (주문완료/상세, 연락처변경, 구독완료/상세)
    if (deliveryInfoReturnable) {
        const apiPayload = {
            ...deliveryInfoPayloadSelector(getState()),
            orderNo: seqID,
            addressBy: addressBy || 'orderResult',
        }
        try {
            await orderManagerApis.changeDelivery(apiPayload)
        } catch (e) {
            if (isApiError(e)) {
                const code = e.response?.data?.result?.code
                const merchantNames = e.response?.data?.result?.merchantNames ?? ''
                const merchantNameMessage = `${merchantNames ? `\n(판매자명: ${merchantNames})` : ''}`
                const deliveryErrorMessage =
                    code === 'MODIFY_ORDER_DELIVERY_ADDRESS_MULTIPLE_MERCHANT_UNMODIFIABLE_DELIVERY_PLACE'
                        ? `${ChangeDeliveryRequestFailMessage[code]}${merchantNameMessage}`
                        : ChangeDeliveryRequestFailMessage[code] || DefaultAddressBookRequestFailMessage

                return rejectWithValue({code, message: deliveryErrorMessage})
            }
        }

        const actionPayload: DeliveryInfoApplyPayload = {
            type: 'deliveryInfo',
            content: {
                deliveryAddress: apiPayload.orderDeliveryAddress,
                isParticularInput: apiPayload.uniqueMemo,
                memos: apiPayload.memos,
            },
        }

        if (addressBy === 'scheduledOrderResult') {
            /** [TODO] 구독 상세 구버전 스펙 대응 (~2025년 2월 말 정기배포 혹은 3월 초 정기배포 중 제거) */
            window.opener?.postMessage('SUCCESS', '*')
        }

        window.opener?.postMessage(
            {
                key: DELIVERY_MANAGER_COMPLETED,
                content: 'SUCCESS',
            },
            '*',
        )

        return actionPayload
    }

    return null
})

export const handlePersonalCustomsCodeValidationError = createAsyncThunk<PersonalCustomsErrorCode, string>(
    'deliveryManager/handleValidationError',
    async (errorCode) => {
        if (errorCode === 'PERSONAL_CUSTOMS_CODE_INPUT') {
            return 'CHANGED'
        } else if (errorCode === 'PERSONAL_CUSTOMS_CODE_INPUT_FAIL') {
            return 'INPUT'
        }
        return 'IDLE'
    },
)

export const closeWithSuccess = createAsyncThunk<
    void,
    void,
    {
        state: {
            deliveryManager: DeliveryManagerState
            common: CommonState
        }
    }
>('deliveryManager/closeWithSuccess', async (_, {dispatch, getState}) => {
    clearStorage()

    const {
        options: {returnUrl, type, postMessageKey},
        status: {historyCountFromBase},
    } = deliveryManagerSelector(getState())

    const {isResult, isDetail, isCheckout} = orderPhaseInfoSelector(getState())
    const {isShoppingSubscription} = pageSetInfoSelector(getState())

    const isOrderWeb = isCheckout || isResult || isDetail || isShoppingSubscription

    const {addressReturnable} = checkReturnRecords(type || '')

    if (returnUrl) {
        // 우편번호검색 케이스에서는 applyAddress에서 처리. 여기서 한번더 푸시하면 쿼리가 증발한다.
        !addressReturnable && dispatch(changeLocation({type: 'PUSH', url: returnUrl}))
    } else if (postMessageKey) {
        dispatch(changeLocation({type: 'CLOSE'}))
    } else if (isNaverShopping && !isOrderWeb) {
        // 외부진입(쇼핑앱) - 뒤로가기 시도 후 탭이 닫히지 않는다면 닫기
        dispatch(changeLocation({type: 'BACK', close: true}))
    } else if (isMobileDisplay && historyCountFromBase >= 1) {
        dispatch(go(-historyCountFromBase))
    } else if (isMobileDisplay && historyCountFromBase === 0) {
        dispatch(changeLocation({type: 'BACK'}))
    } else {
        dispatch(changeLocation({type: 'CLOSE'}))
    }
})

export const closeWithError = createAsyncThunk<
    void,
    void,
    {
        state: {
            deliveryManager: DeliveryManagerState
            common: CommonState
        }
    }
>('deliveryManager/closeWithError', (_, {dispatch, getState}) => {
    clearStorage()

    const {
        options: {returnUrl},
    } = deliveryManagerSelector(getState())
    const {isResult, isDetail, isCheckout} = orderPhaseInfoSelector(getState())
    const {isShoppingSubscription} = pageSetInfoSelector(getState())

    const isOrderWeb = isCheckout || isResult || isDetail || isShoppingSubscription

    if (isNaverShopping && !isOrderWeb) {
        dispatch(changeLocation({type: 'BACK', close: true}))
    } else if (isMobileDisplay) {
        // 외부 모바일페이지에서 접근한 경우 뒤로가기처리
        dispatch(changeLocation({type: 'BACK'}))
    } else {
        dispatch(changeLocation(returnUrl ? {type: 'PUSH', url: returnUrl} : {type: 'CLOSE'}))
    }
})

const ZipCodeCloseType = {
    CLOSE: 'close',
    BACK: 'back',
} as const

/** 우편번호 검색 화면 close 대응 */
export const closeZipCodeSearch = createAsyncThunk<
    void,
    void,
    {
        state: {
            deliveryManager: DeliveryManagerState
        }
    }
>('deliveryManager/closeZipCodeSearch', (_, {dispatch, getState}) => {
    // 우편번호검색 및 주소선택 시 웹스토리지에 저장했던 주소정보 제거
    const saveStoragePayload = saveStoragePayloadSelector(getState())
    saveDeliveryManagerSessionStorage({
        ...saveStoragePayload,
        searchQuery: getInitialAddressSearchQuery(),
        address: null,
        deliveryAddress: null,
    })

    const {
        options: {type: openCase, postMessageKey, closeType, returnUrl},
    } = deliveryManagerSelector(getState())

    // 외부진입 - iframe으로 열린 경우 postMessage로 닫기 요청
    const isIframEnv = window.location !== window.parent.location
    if (isIframEnv && postMessageKey) {
        sendIframePostmessage({key: postMessageKey, content: null})
        return
    }

    // 외부진입 - query에 closeType이 있으면 closeType에 따라 이동
    if (closeType) {
        dispatch(changeLocation(closeType === ZipCodeCloseType.BACK ? {type: 'BACK'} : {type: 'CLOSE'}))
        return
    }

    // 외부진입 - returnUrl이 있으면 검증 후 이동
    if (returnUrl) {
        if (isSafeUrl(returnUrl)) {
            dispatch(changeLocation({type: 'REPLACE', url: returnUrl}))
        } else {
            dispatch(systemAlert({type: 'ALERT', message: unsafeUrlMessage}))
            dispatch(
                loggingAction({
                    type: 'info',
                    info: {
                        message: unsafeUrlMessage,
                    },
                }),
            )
        }
        return
    }

    // 배송지변경창 openCase 로 분기
    const type = (() => {
        // 외부 우편번호검색으로 진입한경우
        // pc 닫기 mo 뒤로가기
        if (openCase === DELIVERY_MANAGER_TYPE.SELECT_ADDRESS_NORMAL) {
            return isMobileDisplay ? 'BACK' : 'CLOSE'
        }

        // 배송지리스트 > 배송지수정화면에서 진입한 경우
        // pc/mo 뒤로가기
        if (initWithDeliveryAddressListCase.includes(openCase || '')) {
            return 'BACK'
        }

        // 5자리우편번호/새주소 진입한 경우
        // pc 닫기 mo 뒤로가기
        if (initWithAddressSearchCase.includes(openCase || '')) {
            return isMobileDisplay ? 'BACK' : 'CLOSE'
        }

        // 디폴트
        return isMobileDisplay ? 'BACK' : 'CLOSE'
    })()

    dispatch(changeLocation({type}))
})

// 배송지수정화면 닫기 액션
export const closeAddressForm = createAsyncThunk<
    void,
    void,
    {
        state: {
            router: RouterState
            deliveryManager: DeliveryManagerState
        }
    }
>('deliveryManager/closeAddressForm', (_, {dispatch, getState}) => {
    const state = getState()
    const {
        deliveryManager: {
            options: {type},
            status: {historyCountFromBase},
        },
    } = state
    const {isDeliveryManagerPathName} = getPathNameInfo(window.location.href)
    const isInternalMobileScreen = isMobileDisplay && !isDeliveryManagerPathName()

    // 배송지수정화면에서 이탈케이스가 아닌 경우에만 웹스토리지에 저장했던 주소정보 제거
    if (closeDeliveryManagerWithFormConfirmTypes.includes(type || '') && !isInternalMobileScreen) {
        clearStorage()
    } else {
        const saveStoragePayload = saveStoragePayloadSelector(getState())
        saveDeliveryManagerSessionStorage({
            ...saveStoragePayload,
            searchQuery: getInitialAddressSearchQuery(),
            address: null,
            deliveryAddress: null,
        })
    }

    const shouldClose = !isMobileDisplay && closeDeliveryManagerWithFormConfirmTypes.includes(type || '')
    if (shouldClose) {
        dispatch(changeLocation({type: 'CLOSE'}))
    } else {
        dispatch(go(-(historyCountFromBase ?? 1)))
    }
})

export const closeAddressList = createAsyncThunk<
    void,
    void,
    {
        state: {
            common: CommonState
        }
    }
>('deliveryManager/closeAddressList', async (_, {dispatch, getState}) => {
    const {isResult, isDetail, isCheckout} = orderPhaseInfoSelector(getState())
    const {isShoppingSubscription} = pageSetInfoSelector(getState())

    const isOrderWeb = isCheckout || isResult || isDetail || isShoppingSubscription
    if (isNaverShopping && !isOrderWeb) {
        dispatch(changeLocation({type: 'BACK', close: true}))
    } else {
        dispatch(changeLocation({type: 'BACK'}))
    }
})

export const clearDeliveryManagerStore = createAction('deliveryManager/clearDeliveryManagerStore')
